import { Button } from "common/components/ui/Button";
import { DialogDescription, DialogTitle } from "common/components/ui/Dialog";
import { DrawerDescription, DrawerTitle } from "common/components/ui/Drawer";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { Campaign } from "common/types";

import useCampaignMutations from "../../../datahooks/useCampaignMutations";
import { startCampaignsBulkAction } from "../../../utils";

type Actions = "start" | "pause" | "delete";

interface CampaignsActionsProps {
  action: Actions;
  selectedCampaigns: Campaign[];
  onBack: () => void;
  onClose: () => void;
}

export default function CampaignsActions({
  action,
  selectedCampaigns,
  onBack,
  onClose,
}: CampaignsActionsProps) {
  const isDesktop = useTwBreakpoint("lg");
  const { deleteCampaign, isDeletingCampaign } = useCampaignMutations();

  function deleteCampaigns() {
    startCampaignsBulkAction(
      (campaignId, abortSignal) => deleteCampaign({ campaignId, abortSignal }),
      selectedCampaigns,
      (finishedItemsCount) =>
        finishedItemsCount === selectedCampaigns.length
          ? "Campaigns deleted"
          : `Deleted ${finishedItemsCount}/${selectedCampaigns.length} campaigns`,
      true,
    );

    onClose();
  }

  const actionTypes: Record<
    Actions,
    {
      label: string;
      onAction: () => void;
    }
  > = {
    start: {
      label: "Start",
      onAction: () => {},
    },
    pause: {
      label: "Pause",
      onAction: () => {},
    },
    delete: {
      label: "Delete",
      onAction: deleteCampaigns,
    },
  };

  const { label, onAction } = actionTypes[action];

  const campaignsCount = selectedCampaigns.length;
  const campaignsLabel = campaignsCount > 1 ? "campaigns" : "campaign";

  const Title = isDesktop ? DialogTitle : DrawerTitle;
  const Description = isDesktop ? DialogDescription : DrawerDescription;

  return (
    <>
      <Title>
        {label} {campaignsLabel}?
      </Title>
      <Description className="mb-8">
        Are you sure you want to {label.toLowerCase()} {campaignsCount}{" "}
        {campaignsLabel}?
      </Description>

      <div className="flex items-center justify-between gap-x-2.5">
        <Button
          className="lg:flex-1"
          variant="secondary-black"
          onClick={onBack}
          disabled={isDeletingCampaign}
        >
          Cancel
        </Button>

        <Button
          className="lg:flex-1"
          variant={action === "delete" ? "outline-danger" : "default-black"}
          onClick={onAction}
          isLoading={isDeletingCampaign}
        >
          {label} {campaignsLabel}
        </Button>
      </div>
    </>
  );
}
